'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { PublicHeroSmokeBoundary } from '@/components/public/public-hero-smoke-boundary';
import { usePathname } from 'next/navigation';
import { hasSiteScenery, isFocusedReaderRoute } from '@/lib/site-scenery';

// Dynamically import the cursor with SSR disabled
const SplashCursor = dynamic(() => import('./SplashCursor'), {
  ssr: false,
});

function canRenderSplashCursor() {
  if (process.env.NEXT_PUBLIC_ENABLE_SPLASH_CURSOR === 'false') {
    return false;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const isFirefox = window.navigator.userAgent.toLowerCase().includes('firefox');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return !isFirefox && !reducedMotion;
}

export default function SplashCursorWrapper() {
  const pathname = usePathname();
  const [shouldRender, setShouldRender] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENABLE_SPLASH_CURSOR === 'false') {
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const coarsePointer = window.matchMedia('(pointer: coarse)');
    const update = () => { setShouldRender(canRenderSplashCursor()); setMobile(coarsePointer.matches || navigator.maxTouchPoints > 0); };
    const timeoutId = window.setTimeout(update, 0);
    reducedMotion.addEventListener('change', update);
    coarsePointer.addEventListener('change', update);

    return () => {
      window.clearTimeout(timeoutId);
      reducedMotion.removeEventListener('change', update);
      coarsePointer.removeEventListener('change', update);
    };
  }, []);

  // Enable touch decoration on the public/account surfaces and welcome only.
  // Lumina and admin keep their existing mobile appearance.
  if (!shouldRender || isFocusedReaderRoute(pathname)
    || (mobile && pathname !== '/welcome' && !hasSiteScenery(pathname))) return null;

  return <PublicHeroSmokeBoundary><SplashCursor DYE_RESOLUTION={mobile ? 256 : 768} SIM_RESOLUTION={mobile ? 64 : 128}
    PRESSURE_ITERATIONS={mobile ? 8 : 20} SHADING={!mobile} MAX_DPR={mobile ? 1 : Number.POSITIVE_INFINITY}
    FRAME_RATE={mobile ? 30 : 60} /></PublicHeroSmokeBoundary>;
}
